import { emailStandard } from "@/common/helper/custom-validator";
import { ServiceProviderSignUp } from "@/common/model/serviceprovider/service-provider-model";
import ServiceProviderServices from "@/common/services/service-provider-service";
import useVuelidate from "@vuelidate/core";
import { email, required } from "@vuelidate/validators";
import { filter, find, orderBy } from "lodash";
import { DateTime } from "luxon";
import { defineComponent } from "vue";
import { TimeZoneModel } from "../common/model/time-zone-model";
import { useQuasar } from 'quasar'
import { Enums } from "@/common/helper/enum";
import { countryData } from "@/common/helper/country-codes";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import { detectRegionFromGeolocation } from "@/common/helper/region-helper";

export default defineComponent({
    name: "singup",
    setup: () => ({ v$: useVuelidate(), q$ : useQuasar() }),

    /************************************/
    /* Property Section *
      /************************************/

    computed: {
        isServiceProviderNameInvalid(): boolean {
            if (this.v$.serviceProvider && this.v$.serviceProvider.serviceProviderName && this.v$.serviceProvider.serviceProviderName.$dirty) {
                return this.v$.serviceProvider.serviceProviderName.$invalid;
            }

            return false;
        },

        isTimeZoneInvalid(): boolean {
            if (this.v$.serviceProvider && this.v$.serviceProvider.timeZone && this.v$.serviceProvider.timeZone.$dirty) {
                return this.v$.serviceProvider.timeZone.$invalid;
            }

            return false;
        },

        isRegionInvalid(): boolean {
            return this.regionTouched && !this.serviceProvider.region;
        },

        isRegionAcknowledgedInvalid(): boolean {
            return this.regionAcknowledgedTouched && !this.regionAcknowledged;
        },

        isFirstNameInvalid(): boolean {
            if (this.v$.serviceProvider && this.v$.serviceProvider.firstName && this.v$.serviceProvider.firstName.$dirty) {
                return this.v$.serviceProvider.firstName.$invalid;
            }

            return false;
        },

        isTechnicianCountInvalid(): boolean {
            if (this.v$.serviceProvider && this.v$.serviceProvider.noOfTechnicians && this.v$.serviceProvider.noOfTechnicians.$dirty) {
                return this.v$.serviceProvider.noOfTechnicians.$invalid;
            }

            return false;
        },

        isLastNameInvalid(): boolean {
            if (this.v$.serviceProvider && this.v$.serviceProvider.lastName && this.v$.serviceProvider.lastName.$dirty) {
                return this.v$.serviceProvider.lastName.$invalid;
            }

            return false;
        },

        isEmailInvalid(): boolean {
            if (this.v$.serviceProvider && this.v$.serviceProvider.email && this.v$.serviceProvider.email.$dirty) {
                return this.v$.serviceProvider.email.$invalid;
            }

            return false;
        },

        showRequiredValidationErrorForEmail(): boolean {
            if (this.isEmailInvalid) {
                return this.v$.serviceProvider.email.required.$invalid
            }
            return false;
        },
        showEmailValidationErrorForEmail(): boolean {
            if (this.isEmailInvalid) {
                return this.v$.serviceProvider.email.email.$invalid;
            }
            return false;
        },
        showEmailStandardValidationErrorForEmail(): boolean {
            if (this.isEmailInvalid) {
                return this.v$.serviceProvider.email.emailStandard.$invalid;
            }
            return false;
        },
        isSignInWithInvalid(): boolean {
            if (this.v$.signInWith && this.v$.signInWith.$dirty) {
                return this.v$.signInWith.$invalid;
            }
            return false;
        },

        getCountries() {
            return countryData;
        },

        showBadEmailError(): boolean {
            return this.badEmailError != null && this.badEmailError.length > 0;
        },

        isSoloTechnician(): boolean {
            return this.serviceProvider.noOfTechnicians === Enums.NoOfTechnicians.JustMe;
        },

        calendlyUrl(): string {
            return this.isSoloTechnician
                ? 'https://calendly.com/gorelo-team/trial'
                : 'https://calendly.com/gorelo-team/onboarding';
        },

        calendlyTitle(): string {
            return this.isSoloTechnician ? 'Trial Chat' : 'Onboarding';
        },

        calendlySubtitle(): string {
            return this.isSoloTechnician ? 'Book a call to discuss the basics' : 'Book with the founders';
        },
    },

    /************************************/
    /* Watch Section *
      /************************************/

    watch: {
       
    },

    /************************************/
    /* Data Object Section *
      /************************************/

    data() {
        return {
            timeZoneList: [] as TimeZoneModel[],

            serviceProvider: {
                serviceProviderName: "" as string,
                timeZone: "" as string,
                firstName: "" as string,
                lastName: "" as string,
                email: "" as string,
                serviceProviderType: 1 as number,
                turnstileToken: "" as string,
                promotionCode: "" as string,
                isLoginWithAzureAd: false as boolean,
                phone: "" as string,
                noOfTechnicians: null as any,
                region: null as any
            } as ServiceProviderSignUp.ServiceProvider,

            widgetId: null,
            isSubmitting: false as boolean,
            isSignupCompleted: false as boolean,
            isUnderReview: false as boolean,
            willAccountLoginWithAzureAD: false as boolean,
            technicianOptions: [ 
                { text: 'Just me', value: Enums.NoOfTechnicians.JustMe },
                { text: '2-4', value: Enums.NoOfTechnicians.TwotoFour },
                { text: '5-9', value: Enums.NoOfTechnicians.FivetoNine },
                { text: '10-19', value: Enums.NoOfTechnicians.tentoNineteen },
                { text: '20+', value: Enums.NoOfTechnicians.tweentyPlus },

            ],
            serviceProviderTypeOptions: [
                { text: 'Managed Service Provider', value: 1 },
                { text: 'Enterprise', value: 2 },
            ],
            regionUsWest: Enums.Region.UsWest,
            regionAustralia: Enums.Region.Australia,
            regionAcknowledged: false as boolean,
            regionAcknowledgedTouched: false as boolean,
            regionTouched: false as boolean,
            signInWith: "" as string,

            counter: 30 as number,
            isCounting: false as boolean,
            signupStep: 1 as number,
            isOTPVerified: false as boolean,
            isCellPhoneNumberValid: false as boolean,
            cellPhone: "" as string,
            otpTimerIntervalId: null as any,
            isOTPSent: false as boolean,
            otpCode: "" as string,
            contactCountry: {
                name: "",
                Iso: "",
                countryCode: "",
                image: "",
            } as any,
            badEmailError: "" as string
        }
    },

    /************************************/
    /* Validation Section *
      /************************************/

    validations: {
        serviceProvider: {
            serviceProviderName: { required },
            firstName: { required },
            lastName: { required },
            email: { required, email, emailStandard },
            serviceProviderType: { required },
            timeZone: { required },
            noOfTechnicians: { required },
        },
        signInWith: {
             required ,
        },

        $validationGroups: ['serviceProvider'],
    },

    /************************************/
    /* Method Section *
      /************************************/

    methods: {

        onClickReloadPage() {
            window.location.reload();
        },

        openCalendlyLink() {
            window.open(this.calendlyUrl, '_blank');
        },

        goToLoginPage() {
            let signInRedirectURI = import.meta.env.VITE_goreloWebBaseURL;
            window.location.replace(signInRedirectURI);
        },

        async validateAndGoToNextStep(){
            this.isSubmitting = true;
            this.v$.serviceProvider!.$touch();
            this.v$.signInWith!.$touch();
            if (this.v$.serviceProvider!.$invalid || this.v$.signInWith!.$invalid) {
                this.isSubmitting = false;
                return;
            }
            this.serviceProvider.isLoginWithAzureAd = this.signInWith == "azure";
            this.signupStep = 2;
            this.isSubmitting = false;
        },

        selectRegion(region: number) {
            this.serviceProvider.region = region;
            this.regionTouched = true;
        },

        async validateRegionAndGoToNextStep() {
            this.isSubmitting = true;
            this.regionTouched = true;
            this.regionAcknowledgedTouched = true;

            if (!this.serviceProvider.region || !this.regionAcknowledged) {
                this.isSubmitting = false;
                return;
            }

            try {
                await ServiceProviderServices.validateServiceProvider(this.serviceProvider, this.serviceProvider.region);
                this.signupStep = 3;
            }
            catch (e) {
                console.error("Error in signup process");
                console.error(e);

                if (e != null && e.response != null && e.response.data != null && Array.isArray(e.response.data) && e.response.data.length > 0) {
                    this.badEmailError = e.response.data[0].errorMessage;
                }
            }
            finally {
                this.isSubmitting = false;
            }
        },

        goToPreviousPage(){
            if (this.signupStep === 3) {
                this.signupStep = 2;
                return;
            }

            this.signupStep = 1;
            this.resetTurnstile();
            this.loadTurnstileWidget();
        },

        async sendOTPCode(){
            this.startTimer();
            try {
                await ServiceProviderServices.sendOTP({
                    channel: Enums.OTPChannel.sms,
                    phoneOrEmail: this.getPhoneNumberWithCode(),
                }, this.serviceProvider.region);
                this.isOTPSent = true;
            }
            catch (e) {
                this.stopTimer();
                console.error("Error in sending OTP code");
                console.error(e);

                let errorMessage = 'Unable to send OTP code';
                if(e?.response?.data?.toLowerCase().includes('blocked by twilio')) 
                    errorMessage = e?.response?.data;

                this.q$.notify({
                    message: errorMessage,
                    color: 'red',
                    position: "top"
                })
            }
        },

        stopTimer() {
            clearInterval(this.otpTimerIntervalId);
            this.isCounting = false;
        },

        getPhoneNumberWithCode(): string {
            const phoneUtil = PhoneNumberUtil.getInstance();
            const number = phoneUtil.parseAndKeepRawInput(this.cellPhone, this.contactCountry.countryCode);
            return phoneUtil.format(number, PhoneNumberFormat.E164);
        },

        startTimer() {
            if (this.isCounting) return;

            this.counter = 30;
            this.isCounting = true;

            this.otpTimerIntervalId = setInterval(() => {
                this.counter--;
                if (this.counter === 0) {
                    this.stopTimer();
                }
            }, 1000);
        },

        async verifyOTPCode() {
            try {
                var response = await ServiceProviderServices.verifyOTP({
                    code: this.otpCode,
                    phoneOrEmail: this.getPhoneNumberWithCode(),
                }, this.serviceProvider.region);
                if(response?.status === 'approved') {
                    this.isOTPVerified = true;
                }
                else {
                    this.q$.notify({
                        message: "Invalid OTP code",
                        color: 'red',
                        position: "top"
                    })
                }
            } catch (e) {
                this.stopTimer();
                console.error("Error in verifying OTP code");
                console.error(e);
                
                let errorMessage = 'Invalid OTP code';
                if(e?.response?.data?.toLowerCase().includes('blocked by twilio')) 
                    errorMessage = e?.response?.data;
                
                this.q$.notify({
                    message: errorMessage,
                    color: 'red',
                    position: "top"
                });
            }
        },

        async signupFormSubmit() {
            await this.verifyOTPCode();
            if(!this.isOTPVerified) 
                return;

            console.log("OTP verified");
            console.log(this.serviceProvider, this.signInWith);

            this.isSubmitting = true;
            try {
                this.serviceProvider.phone = this.getPhoneNumberWithCode();
                var response = await ServiceProviderServices.saveServiceProvider(this.serviceProvider, this.serviceProvider.region);
                if(response){
                    if(response.status == "created")
                        this.isSignupCompleted = true;
                    else if(response.status == "review")
                        this.isUnderReview = true;
                }
                this.willAccountLoginWithAzureAD = this.serviceProvider.isLoginWithAzureAd;
                this.clearFormData();
                this.v$.serviceProvider!.$reset();
                // Send event to Twillio Segment
                (window as any).analytics.track("signup form submitted", {
                    email_entered: true,
                    email_domain_type: "paid",
                    validation_successful: true
                });
            }
            catch (e) {
                console.error("Error in signup process");
                console.error(e);

                if (e != null && e.response != null && e.response.data != null && Array.isArray(e.response.data) && e.response.data.length > 0) {
                    this.q$.notify({
                        message: e.response.data[0].errorMessage,
                        color: 'red',
                        position: "top"
                    })
                }
            }
            finally {
                this.isSubmitting = false;
                this.resetTurnstile();
            }
        },


        touchValidation(propertyName: string) {
            if (this.v$.serviceProvider && this.v$.serviceProvider[propertyName]) {
                this.v$.serviceProvider[propertyName].$touch();
            }
            if (propertyName == "signInWith" && this.v$.signInWith) {
                this.v$.signInWith!.$touch();
            }
        },

        clearFormData() {
            this.serviceProvider.serviceProvideName = "";
            this.serviceProvider.firstName = "";
            this.serviceProvider.lastName = "";
            this.serviceProvider.email = "";
            this.serviceProvider.timeZone = "";
        },

        status(validation: any) {
            return {
                error: validation.$error,
                dirty: validation.$dirty,
            };
        },

        resetTurnstile() {
            if ((window as any).turnstile && this.widgetId) {
                (window as any).turnstile.reset(this.widgetId);
            }
        },

        async fetchTimeZoneList() {
            try {
                let response: TimeZoneModel[] = await ServiceProviderServices.getServieproviderTimeZoneList();

                for (let i = 0; i < response.length; i++) {
                    let zone: string = DateTime.utc()
                        .setZone(response[i].value)
                        .toFormat("ZZ");
                    response[i].nameWithZone = "(" + zone + ") " + response[i].text;
                    response[i].isNegative = zone.includes("-");
                }

                let negativeValuedZones: TimeZoneModel[] = filter(
                    response,
                    function (timeZone: TimeZoneModel) {
                        return timeZone.isNegative == true;
                    }
                );

                let postiveValuedZones: TimeZoneModel[] = filter(
                    response,
                    function (timeZone: TimeZoneModel) {
                        return timeZone.isNegative == false;
                    }
                );

                //sort values with minus in desc order and values with plus in asc order
                negativeValuedZones = orderBy(
                    negativeValuedZones,
                    (timeZone) => timeZone.nameWithZone,
                    "desc"
                );
                postiveValuedZones = orderBy(
                    postiveValuedZones,
                    (timeZone: any) => timeZone.nameWithZone,
                    "asc"
                );

                this.timeZoneList = negativeValuedZones.concat(postiveValuedZones);
            }
            catch (e) {
                console.error("Error in fetching timezone list");
                console.error(e);
            }
        },

        preselectDefaultValues() {
            let browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            let timezone = find(this.timeZoneList, function (timezone: TimeZoneModel) {
                return timezone.value == browserTimezone;
            });

            if (timezone != null) {
                this.serviceProvider.timeZone = browserTimezone;
            }
        },

        async preselectRegion() {
            this.serviceProvider.region = await detectRegionFromGeolocation();
        },

        async loadData() {
            await this.fetchTimeZoneList();
            this.preselectDefaultValues();
            this.preselectRegion();
        },

        loadTurnstileWidget() {
            // Wait for next tick to ensure DOM is ready
            this.$nextTick(() => {
                if ((window as any).turnstile) {
                    this.widgetId = (window as any).turnstile.render("#cf-turnstile", {
                        sitekey: import.meta.env.VITE_turnstileSiteKey,
                        theme: "dark",
                        callback: (token) => {
                            this.serviceProvider.turnstileToken = token;
                        },
                    });
                }
            });
        },

        phoneHasAlphabets(phone: string): boolean {
            if(!phone || !phone.length)
                return false;

            for (let i = 0; i < phone.length; i++) {
                if (phone[i].toLowerCase() >= "a" && phone[i].toLowerCase() <= "z") {
                    return true;
                }
            }

            return false;
        },

        isNumberValid(phoneNumber: string) {
            //validator lib takes least length 2 to check validation
            if(!phoneNumber || phoneNumber.length <= 1 || this.phoneHasAlphabets(phoneNumber))
                return false;

            if(!this.contactCountry.countryCode)
                return false;
            
            const phoneUtil = PhoneNumberUtil.getInstance();
            try {
                const number = phoneUtil.parseAndKeepRawInput(phoneNumber, this.contactCountry.countryCode);
                return phoneUtil.isValidNumberForRegion(number, this.contactCountry.countryCode);
            } catch (err) {
                console.log(err);
                return false;
            }
        },

        statusCellPhone() {
            return {
                error: !this.isCellPhoneNumberValid,
                dirty: !this.isCellPhoneNumberValid,
            };
        },
    },

    /************************************/
    /* Created Section *
      /************************************/

    created() {
        this.loadData();
    },

    mounted() {
        this.loadTurnstileWidget();
    },
});
